/**
 * Single source of truth for bolt-on module marketing copy and onboarding.
 *
 * Add new modules here only — they automatically appear in:
 * - Welcome / Add-ons onboarding (via GET /api/addons/catalog)
 * - System admin → New company enable checkboxes (same API)
 * - Server create-company provisioning (imports this catalog)
 *
 * Set `showInOnboarding: true` to include a module in those surfaces.
 */
export type AddonCatalogEntry = {
  id: string;
  name: string;
  /** One-line summary for cards / welcome teaser */
  shortDescription: string;
  /** How this helps the company operationally */
  howItHelps: string;
  /** Include in onboarding welcome + optional activate step + new-company checkboxes */
  showInOnboarding: boolean;
  /** API path that returns { isFree, stripeReady } for this company (query companyCode) */
  optionsPath: string | null;
  /** Tailwind colour token for accent styling in the wizard */
  accent: "indigo" | "violet" | "emerald" | "cyan" | "amber";
};

export const ADDON_CATALOG: AddonCatalogEntry[] = [
  {
    id: "qa-verifications",
    name: "QA Verifications",
    shortDescription:
      "Run automated employee verification checks and produce printable / downloadable compliance reports.",
    howItHelps:
      "Gives managers a structured way to evidence that safety-critical checks are being completed, with a clear audit trail for audits and assurance reviews.",
    showInOnboarding: true,
    optionsPath: "/api/qa-verifications/options",
    accent: "indigo",
  },
  {
    id: "task-assignment",
    name: "Task assignment",
    shortDescription:
      "Assign tasks to users, optionally link them to an employee record, and track status in the Tasks sidebar.",
    howItHelps:
      "Keeps operational follow-ups visible and owned — reducing missed actions between managers, assessors, and front-line staff.",
    showInOnboarding: true,
    optionsPath: "/api/task-module/options",
    accent: "violet",
  },
  {
    id: "safety-briefs",
    name: "Safety Briefs",
    shortDescription:
      "Define brief types and delivery windows, then record attendance (in-person or remote) with dashboard visuals.",
    howItHelps:
      "Records who received critical safety messaging and when, supporting compliance culture and post-incident review.",
    showInOnboarding: true,
    optionsPath: "/api/task-module/options",
    accent: "emerald",
  },
  {
    id: "trainee-driver",
    name: "Trainee",
    shortDescription:
      "Build training schedules, enrol trainees, track hours, dual-signed feedback, and route learning passes.",
    howItHelps:
      "Centralises trainee progression so training managers can see progress, attendance, and support needs in one place.",
    showInOnboarding: true,
    optionsPath: "/api/trainee-driver/options",
    accent: "indigo",
  },
  {
    id: "driver-reports",
    name: "Driver Reports",
    shortDescription:
      "Capture signed operational driver reports and sync them across the company for review and export.",
    howItHelps:
      "Standardises turn / headcode / conditions / incident narratives so issues are recorded promptly and ready for investigation workflows.",
    showInOnboarding: true,
    optionsPath: "/api/driver-reports/options",
    accent: "cyan",
  },
  {
    id: "leave-absence",
    name: "Leave & Absence",
    shortDescription:
      "Track annual leave entitlements, sickness, and other absences with manager approval and reporting.",
    howItHelps:
      "Gives managers a clear view of entitlement, pending requests, and absence history — with email approval and a full audit trail on each employee record.",
    showInOnboarding: true,
    optionsPath: "/api/leave-absence/options",
    accent: "amber",
  },
  {
    id: "medication-checks",
    name: "Medication Checks",
    shortDescription:
      "Record medication declarations and occupational health med checks on each employee medical record.",
    howItHelps:
      "Keeps medication fitness checks auditable — with optional employee self-submit for manager review, OH outcomes, and report attachments.",
    showInOnboarding: true,
    optionsPath: "/api/medication-checks/options",
    accent: "emerald",
  },
];

/** Modules shown during onboarding (welcome + activate step) and new-company enablement. */
export function getOnboardingAddons(): AddonCatalogEntry[] {
  return ADDON_CATALOG.filter((a) => a.showInOnboarding);
}

export function getAddonCatalogEntry(id: string): AddonCatalogEntry | undefined {
  return ADDON_CATALOG.find((entry) => entry.id === id);
}
